// CreateDetails.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { googleads, bingads } from "../assets/index";
import { connectionUrl } from "./CreateProject";

const CreateDetails = () => {
  const { projectId } = useParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [googleAdsAccounts, setGoogleAdsAccounts] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [email, setEmail] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchHeaderData = async () => {
      try {
        // Assuming the email is in localStorage, as per createproject.js
        const userEmail = localStorage.getItem("email");
        if (userEmail) {
          const response = await axios.post(
            "https://api.confidanto.com/header-data",
            { email: userEmail }
          );
          if (response.data) {
            setEmail(response.data.email);
          }
        }
      } catch (error) {
        console.error("Error fetching header data:", error);
      }
    };

    const searchParams = new URLSearchParams(location.search);
    const ids = searchParams.get("customer_ids");
    const currencyTypes = searchParams.get("currencyType");
    const refreshToken = searchParams.get("refresh_token");

    if (ids && currencyTypes && refreshToken) {
      fetchHeaderData();
      alert("Google Ads Account Connected Successfully...!");
      setCurrentStep(2); // Move to selection step

      const idsArray = ids.split(",");
      const currencyTypesArray = currencyTypes.split(",");

      if (idsArray.length === currencyTypesArray.length) {
        const accounts = idsArray.map((id, index) => ({
          id: id,
          currency: currencyTypesArray[index],
        }));
        setGoogleAdsAccounts(accounts);
      } else {
        console.error(
          "Mismatch between number of customer IDs and currency types."
        );
        setErrorMessage(
          "Error processing Google Ads accounts. Please try connecting again."
        );
      }
    }
  }, [location.search]);

  const handleConnectAccount = (type) => {
    try {
      if (type === "microsoft") {
        window.location.href = `${connectionUrl}/microsoft_ads/authorize`;
      } else {
        // google ads
        window.location.href = `${connectionUrl}/authorize`;
      }
    } catch (error) {
      console.error("Error during authorization:", error);
    }
  };

  const handleSaveConnection = async () => {
    if (!selectedCustomerId) {
      setErrorMessage("Please select a Google Ads account to continue.");
      return;
    }

    try {
      const searchParams = new URLSearchParams(location.search);
      const refreshToken = searchParams.get("refresh_token");
      const userEmail = localStorage.getItem("email");

      const selectedAccount = googleAdsAccounts.find(
        (acc) => acc.id === selectedCustomerId
      );

      if (!selectedAccount || !refreshToken || !userEmail) {
        setErrorMessage(
          "Required connection details are missing. Please try again."
        );
        return;
      }

      const requestData = {
        project_id: projectId,
        customer_id: Number(selectedCustomerId),
        email: userEmail,
        refresh_token: refreshToken,
        currency_type: selectedAccount.currency,
      };

      const response = await axios.post(
        "https://api.confidanto.com/connect-google-ads/connect-with-google-ads",
        requestData
      );

      if (response.status === 201) {
        console.log("Customer ID saved successfully:", response.data);
        navigate(`/project-details/${projectId}`);
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
    } catch (error) {
      console.error("Error saving customer ID:", error);
      if (error.response && error.response.data.message) {
        setErrorMessage(error.response.data.message);
      } else {
        setErrorMessage("Failed to save the connection. Please try again.");
      }
    }
  };

  const StepIndicator = () => (
    <div className="bg-gray-50 border-l border-gray-200 p-8 flex flex-col justify-between w-full lg:w-1/4">
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-6">
          Connection Progress
        </h3>
        <ul className="space-y-5">
          {["Connect Accounts", "Select Account", "Finish"].map(
            (step, index) => (
              <li key={index} className="flex items-center gap-3">
                <div
                  className={`w-6 h-6 flex items-center justify-center rounded-full ${
                    index < currentStep
                      ? "bg-green-500 text-white"
                      : "border border-gray-300 text-gray-400"
                  }`}
                >
                  {index < currentStep - 1 ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                      stroke="currentColor"
                      className="w-4 h-4"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4.5 12.75l6 6 9-13.5"
                      />
                    </svg>
                  ) : (
                    <span className="text-sm">{index + 1}</span>
                  )}
                </div>
                <span
                  className={`${
                    index < currentStep ? "text-gray-800" : "text-gray-400"
                  } text-sm font-medium`}
                >
                  {step}
                </span>
              </li>
            )
          )}
        </ul>
      </div>
      <div className="border-t border-gray-200 mt-10 pt-6">
        <p className="text-sm text-gray-500 mb-3">
          Having trouble? We’re here to help you through the process.
        </p>
        <button className="w-full bg-white border border-gray-300 hover:border-blue-500 text-gray-700 hover:text-blue-600 py-2 rounded-lg font-medium transition-all">
          Contact Us
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col items-center w-full min-h-screen bg-gray-100 justify-center">
      <main className="w-full max-w-6xl flex flex-col lg:flex-row bg-white rounded-2xl shadow-lg overflow-hidden mx-auto mt-6 border border-gray-200">
        <div className="flex-1 px-8 py-10 bg-white">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Connect your Ad accounts
          </h2>
          <p className="text-gray-500 mb-8">
            Connect to an existing project by linking your ad accounts.
          </p>

          {email && (
            <div className="border border-gray-200 rounded-lg p-5 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <img
                    src="https://www.svgrepo.com/show/475656/google-color.svg"
                    alt="Google"
                    className="w-6 h-6"
                  />
                  <h3 className="font-medium text-gray-800">Google Account</h3>
                </div>
                <span className="text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full font-medium">
                  Connected
                </span>
              </div>
              <div className="border rounded-md p-3 flex items-center justify-between bg-gray-50">
                <p className="text-gray-700 text-sm">{email}</p>
              </div>
            </div>
          )}

          <div className="border border-gray-200 rounded-lg p-5 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <img src={googleads} alt="Google Ads" className="w-6 h-6" />
              <h3 className="font-medium text-gray-800">Google Ads Accounts</h3>
            </div>

            {googleAdsAccounts.length > 0 ? (
              <>
                <p className="text-gray-500 text-sm mb-4">
                  Choose a Google Ads account to connect to this project:
                </p>
                <div className="space-y-3">
                  {googleAdsAccounts.map((acc, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between border rounded-md p-3 cursor-pointer hover:bg-gray-100 transition ${
                        selectedCustomerId === acc.id
                          ? "ring-2 ring-indigo-500 bg-indigo-50"
                          : "bg-gray-50"
                      }`}
                      onClick={() => setSelectedCustomerId(acc.id)}
                    >
                      <div>
                        <p className="text-sm font-medium text-gray-700">
                          Google Ads Account
                        </p>
                        <p className="text-xs text-gray-500">
                          Customer ID: {acc.id}
                        </p>
                        <p className="text-xs text-gray-400">
                          Currency: {acc.currency}
                        </p>
                      </div>
                      <button
                        className={`px-4 py-1.5 rounded-md font-medium text-sm ${
                          selectedCustomerId === acc.id
                            ? "bg-green-100 text-green-700 border border-green-400 cursor-not-allowed"
                            : "bg-indigo-600 text-white hover:bg-indigo-700"
                        }`}
                        onClick={() => setSelectedCustomerId(acc.id)}
                        disabled={selectedCustomerId === acc.id}
                      >
                        {selectedCustomerId === acc.id ? "Selected" : "Select"}
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  onClick={handleSaveConnection}
                  className="w-full mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 rounded-md transition disabled:bg-indigo-300"
                  disabled={!selectedCustomerId}
                >
                  Continue
                </button>
              </>
            ) : (
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-4">
                  Used to manage your Ads in Google.
                </p>
                <button
                  className="text-blue-600 text-sm font-medium hover:underline"
                  onClick={() => handleConnectAccount("google")}
                >
                  Connect Google Ads
                </button>
              </div>
            )}
          </div>

          <div className="border border-gray-200 rounded-lg p-5">
            <div className="flex items-center gap-2 mb-3">
              <img src={bingads} alt="Microsoft Ads" className="w-6 h-6" />
              <h3 className="font-medium text-gray-800">Microsoft Ads</h3>
            </div>
            <div className="text-center">
              <p className="text-gray-500 text-sm mb-4">
                Used to manage your Ads in Microsoft.
              </p>
              <button
                className="text-blue-600 text-sm font-medium hover:underline"
                onClick={() => handleConnectAccount("microsoft")}
              >
                Connect Microsoft Ads
              </button>
            </div>
          </div>
          {errorMessage && (
            <p className="text-red-500 text-sm mt-4 text-center">
              {errorMessage}
            </p>
          )}
        </div>
        <StepIndicator />
      </main>
    </div>
  );
};

export default CreateDetails;
